import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import logo from '../../assets/img/logo.webp';
import API from '../../api/api';
import AuthWelcomeShell from './AuthWelcomeShell';
import shellStyles from './AuthWelcomeShell.module.css';

interface ForgetPasswordFormProps {
  onResetSuccess?: (userCode: string) => void;
}

const ForgetPasswordForm: React.FC<ForgetPasswordFormProps> = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setError(null);
    setSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!email) {
      setError('Email is required');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Invalid email format');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await API.post('/auth/forget', { email });
      setSuccess(true);
      setEmail('');
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        setError(error.response.data.message || 'Email not found or an error occurred while processing your request.');
      } else {
        setError('An unexpected error occurred.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthWelcomeShell
      pageLabel="Forgot password"
      leftHeading="Can't access your account?"
      leftDescription="We'll email you a secure link to choose a new password so you can return to your family tree."
    >
      <div className={shellStyles.logoBlock}>
        <Link to="/" aria-label="Home">
          <img src={logo} className={shellStyles.logoImg} alt="iGiTREE logo" />
        </Link>
      </div>
      <h1 className={shellStyles.title}>Reset your password</h1>
      <p className={shellStyles.subtitle}>
        Enter the email you use for iGiTREE and we&apos;ll send you instructions.
      </p>

      <form className="main-input" onSubmit={handleSubmit}>
        {success && (
          <div className="alert alert-success mb-3" role="alert">
            Password reset instructions have been sent to your email.
          </div>
        )}
        {error && (
          <div className="alert alert-danger mb-3" role="alert">
            {error}
          </div>
        )}

        <div className="input-group">
          <span className="input-group-text">
            <i className="fa-sharp fa-light fa-envelope" aria-hidden />
          </span>
          <div className="form-floating">
            <input
              type="email"
              className="form-control"
              name="email"
              id="email"
              placeholder="Email"
              value={email}
              onChange={handleChange}
              required
              autoComplete="email"
            />
            <label htmlFor="email">Email Address</label>
          </div>
        </div>

        <button type="submit" className={shellStyles.btnGradient} disabled={loading}>
          {loading ? 'Sending...' : 'Send reset link'}
        </button>
      </form>

      <div className={shellStyles.linkRow}>
        <Link to="/login" className={shellStyles.link}>
          Back to sign in
        </Link>
      </div>
    </AuthWelcomeShell>
  );
};

export default ForgetPasswordForm;
